import { useMemo } from 'react';

import { MetricBoxConfig, MetricsBoxes } from '@/components/common/DataTable';
import type { PaginationActions, PaginationState } from '@/hooks/usePagination';

interface StatusCount {
  status: string;
  count: number;
}

interface IntakeMetricsBoxesProps {
  paginationState: PaginationState;
  paginationActions: PaginationActions;
  totalCount: number;
  statusCounts?: StatusCount[];
}

/**
 * Metric boxes displayed at the top-right of the Intakes page.
 * Acts as additive filters that work alongside table header filters (firm, creator, etc.).
 * "Total Intakes" clears status/unresponsive filters while preserving other filters.
 *
 * "Chase em down" shows the total count of chase leads needing follow-up today.
 * When clicked, the table activates timezone-aware grouping (Callable Now / Opens at X)
 * so the team can see at a glance which leads are ready to call.
 */
export const IntakeMetricsBoxes = ({
  paginationState,
  paginationActions,
  totalCount,
  statusCounts = [],
}: IntakeMetricsBoxesProps) => {
  const unresponsiveCount =
    statusCounts.find((s) => s.status === 'unresponsive')?.count || 0;
  const unconvertedCount =
    statusCounts.find((s) => s.status === 'unconverted')?.count || 0;
  const chaseTodayCount =
    statusCounts.find((s) => s.status === 'chase_today')?.count || 0;
  const rejectedNeedsNoticeCount =
    statusCounts.find((s) => s.status === 'rejected_needs_notice')?.count || 0;

  const boxes: MetricBoxConfig[] = useMemo(
    () => [
      {
        id: 'total',
        label: 'Total Intakes',
        count: totalCount,
        tooltip: 'Total count of all Finch intakes',
        filters: {},
        isClearAll: true,
      },
      {
        id: 'unresponsive',
        label: 'Unresponsive',
        count: unresponsiveCount,
        tooltip:
          'Intakes with In Progress, Sent for Signature, or Chase status that have 5 or more notes logged',
        filters: {
          unresponsive: 'true',
        },
      },
      {
        id: 'unconverted',
        label: 'Unconverted',
        count: unconvertedCount,
        tooltip:
          'Signed intakes whose matter is still in Intake status (excludes configured firms)',
        filters: {
          unconverted: 'true',
        },
      },
      {
        id: 'chase_today',
        label: 'Chase em down',
        count: chaseTodayCount,
        tooltip:
          'Chase status intakes without activity today (need follow-up). Grouped by timezone so you know who to call now.',
        filters: {
          chase_today: 'true',
        },
      },
      {
        id: 'rejected_needs_notice',
        label: 'Needs Notice',
        count: rejectedNeedsNoticeCount,
        tooltip:
          'Rejected intakes where non-representation notice has not been sent',
        filters: {
          status: 'rejected',
          rejection_email_sent: 'false',
        },
      },
    ],
    [
      totalCount,
      unresponsiveCount,
      unconvertedCount,
      chaseTodayCount,
      rejectedNeedsNoticeCount,
    ],
  );

  return (
    <MetricsBoxes
      boxes={boxes}
      paginationState={paginationState}
      paginationActions={paginationActions}
      metricFilterKeys={[
        'status',
        'unresponsive',
        'unconverted',
        'chase_today',
        'rejection_email_sent',
      ]}
      size="compact"
    />
  );
};
